package com.fantasytenis.shared.core.realtime

import kotlinx.serialization.Serializable

/**
 * Event types for real-time updates from the server.
 */
object EventTypes {
    // Match events
    const val MATCH_STARTED = "match:started"
    const val MATCH_POINT_SCORED = "match:point_scored"
    const val MATCH_GAME_COMPLETED = "match:game_completed"
    const val MATCH_SET_COMPLETED = "match:set_completed"
    const val MATCH_COMPLETED = "match:completed"
    const val MATCH_SUSPENDED = "match:suspended"
    const val MATCH_RESUMED = "match:resumed"

    // Contest events
    const val CONTEST_ENTRY_UPDATE = "contest:entry_update"
    const val CONTEST_LEADERBOARD_UPDATE = "contest:leaderboard_update"
    const val CONTEST_STATUS_CHANGE = "contest:status_change"
    const val CONTEST_PAYOUT_PROCESSED = "contest:payout_processed"

    // Bracket events
    const val BRACKET_PICK_RESULT = "bracket:pick_result"
    const val BRACKET_LEADERBOARD_UPDATE = "bracket:leaderboard_update"
    const val BRACKET_CHALLENGE_STATUS_CHANGE = "bracket:status_change"

    // Player events
    const val PLAYER_SCORE_UPDATE = "player:score_update"
    const val PLAYER_STATUS_CHANGE = "player:status_change"

    // System events
    const val SYSTEM_ANNOUNCEMENT = "system:announcement"
    const val MAINTENANCE_NOTICE = "system:maintenance"

    // Survivor events
    const val SURVIVOR_PICK_RESULT = "survivor:pick_result"
    const val SURVIVOR_ELIMINATION = "survivor:elimination"
    const val SURVIVOR_LEADERBOARD_UPDATE = "survivor:leaderboard_update"
    const val SURVIVOR_ROUND_UPDATE = "survivor:round_update"
}

/**
 * Subscription types for subscribing to different channels.
 */
enum class SubscriptionType {
    MATCH,
    CONTEST,
    BRACKET,
    SURVIVOR,
    USER_ENTRIES,
    GLOBAL
}

/**
 * Connection state for the WebSocket connection.
 */
enum class ConnectionState {
    DISCONNECTED,
    CONNECTING,
    CONNECTED,
    RECONNECTING,
    ERROR
}

/**
 * Base class for all realtime events.
 */
@Serializable
data class RealtimeEvent(
    val id: String,
    val type: String,
    val entityId: String,
    val entityType: String,
    val payload: String, // JSON payload
    val timestamp: Long
)

/**
 * Connection acknowledgment from server.
 */
@Serializable
data class ConnectionAck(
    val clientId: String,
    val authenticated: Boolean,
    val userId: String? = null,
    val timestamp: Long
)

/**
 * Score for a single set in a match.
 */
@Serializable
data class SetScorePayload(
    val homeScore: Int,
    val awayScore: Int,
    val setNumber: Int
)

/**
 * Live score update payload.
 */
@Serializable
data class LiveScorePayload(
    val matchId: String,
    val player1Id: String,
    val player1Name: String,
    val player2Id: String,
    val player2Name: String,
    val setScore: String,
    val gameScore: String,
    val pointScore: String,
    val isTiebreak: Boolean = false,
    val tiebreakScore: String? = null,
    val player1Aces: Int = 0,
    val player2Aces: Int = 0,
    val player1DoubleFaults: Int = 0,
    val player2DoubleFaults: Int = 0,
    val player1Winners: Int = 0,
    val player2Winners: Int = 0,
    val player1UnforcedErrors: Int = 0,
    val player2UnforcedErrors: Int = 0,
    val player1BreakPointsWon: Int = 0,
    val player2BreakPointsWon: Int = 0,
    val player1BreakPointsSaved: Int = 0,
    val player2BreakPointsSaved: Int = 0,
    val serverId: String = "",
    val status: String = "live",
    val winnerId: String? = null,
    val finalScore: String? = null,
    val timestamp: Long,
    val setScores: List<SetScorePayload> = emptyList()
)

/**
 * Contest leaderboard entry.
 */
@Serializable
data class ContestLeaderboardEntryPayload(
    val entryId: String,
    val userId: String,
    val displayName: String,
    val totalPoints: Double,
    val rank: Int,
    val previousRank: Int = 0,
    val payoutAmount: Double = 0.0
)

/**
 * Contest leaderboard update payload.
 */
@Serializable
data class ContestLeaderboardPayload(
    val contestId: String,
    val entries: List<ContestLeaderboardEntryPayload>,
    val timestamp: Long
)

/**
 * Bracket leaderboard entry.
 */
@Serializable
data class BracketLeaderboardEntryPayload(
    val entryId: String,
    val userId: String,
    val displayName: String,
    val bracketName: String,
    val totalPoints: Double,
    val correctPicks: Int,
    val pendingPicks: Int,
    val possiblePoints: Double,
    val rank: Int,
    val previousRank: Int = 0
)

/**
 * Bracket leaderboard update payload.
 */
@Serializable
data class BracketLeaderboardPayload(
    val challengeId: String,
    val entries: List<BracketLeaderboardEntryPayload>,
    val timestamp: Long
)

/**
 * User entry update payload.
 */
@Serializable
data class UserEntryUpdatePayload(
    val entryId: String,
    val contestId: String,
    val userId: String,
    val totalPoints: Double,
    val rank: Int,
    val previousRank: Int = 0,
    val status: String,
    val timestamp: Long
)

/**
 * System announcement payload.
 */
@Serializable
data class AnnouncementPayload(
    val message: String,
    val severity: String = "info" // info, warning, error
)

/**
 * Maintenance notice payload.
 */
@Serializable
data class MaintenancePayload(
    val message: String,
    val scheduledAt: Long? = null,
    val durationMinutes: Int? = null
)

/**
 * Subscription request to server.
 */
@Serializable
data class SubscribeRequest(
    val type: String,
    val entityId: String? = null
)

/**
 * Subscription response from server.
 */
@Serializable
data class SubscribeResponse(
    val success: Boolean,
    val room: String? = null,
    val error: String? = null
)

/**
 * Survivor leaderboard entry payload.
 */
@Serializable
data class SurvivorLeaderboardEntryPayload(
    val entryId: String,
    val userId: String,
    val displayName: String,
    val status: Int = 0,
    val strikesUsed: Int = 0,
    val survivedRounds: Int = 0,
    val lastPickPlayer: String? = null,
    val lastPickResult: Int? = null
)

/**
 * Survivor leaderboard update payload.
 */
@Serializable
data class SurvivorLeaderboardPayload(
    val contestId: String,
    val aliveCount: Int,
    val eliminatedThisRound: Int,
    val entries: List<SurvivorLeaderboardEntryPayload>,
    val timestamp: Long
)

/**
 * Survivor pick result payload.
 */
@Serializable
data class SurvivorPickResultPayload(
    val contestId: String,
    val entryId: String,
    val pickId: String,
    val playerName: String,
    val result: Int,
    val matchScore: String? = null,
    val isEliminated: Boolean,
    val strikesUsed: Int,
    val timestamp: Long
)

/**
 * Survivor elimination payload.
 */
@Serializable
data class SurvivorEliminationPayload(
    val contestId: String,
    val entryId: String,
    val userId: String,
    val displayName: String,
    val eliminatedInRound: Int,
    val timestamp: Long
)
